import { useCallback, useEffect, useRef, useState } from 'react'

const DEFAULT_DURATION_MS = 3000

interface UseToastReturn {
  isVisible: boolean
  message: string
  show: (message: string, durationMs?: number) => void
  hide: () => void
}

/**
 * Simple toast/snackbar state hook. `show` displays the given message and
 * auto-hides it after `durationMs` (default 3000ms). Calling `show` again
 * before the previous timeout fires clears it first, so only one hide is
 * ever scheduled at a time. `hide` dismisses immediately (e.g. close button).
 */
export const useToast = (): UseToastReturn => {
  const [isVisible, setIsVisible] = useState(false)
  const [message, setMessage] = useState('')
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const show = useCallback((nextMessage: string, durationMs: number = DEFAULT_DURATION_MS) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setMessage(nextMessage)
    setIsVisible(true)

    timeoutRef.current = setTimeout(() => {
      setIsVisible(false)
    }, durationMs)
  }, [])

  const hide = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
  }, [])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return { isVisible, message, show, hide }
}
